import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, TextField, CircularProgress } from '@mui/material';
import AddressAutocomplete from './AddressAutocomplete.jsx';
import PropTypes from 'prop-types';

function DoorDelivery ({ onUpdateDeliveryDetails }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authState = useSelector((state) => state.auth);
  const patientAuthState = useSelector((state) => state.patientAuth);
  // Initialize state with localStorage data if available
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [address, setAddress] = useState(
    () => localStorage.getItem('delivery_address') || ''
  );
  const [building, setBuilding] = useState(
    () => localStorage.getItem('delivery_building') || ''
  );
  const [name, setName] = useState(
    () => localStorage.getItem('delivery_name') || ''
  );
  const [email, setEmail] = useState(
    () => localStorage.getItem('delivery_email') || ''
  );

  const [age, setAge] = useState(
    () => localStorage.getItem('delivery_age') || ''
  );
  const [phone, setPhone] = useState(
    () => localStorage.getItem('delivery_phone') || ''
  );
  const [instructions, setInstructions] = useState(
    () => localStorage.getItem('delivery_instructions') || ''
  );

  // Add PropTypes validation
  DoorDelivery.propTypes = {
    onUpdateDeliveryDetails: PropTypes.func.isRequired
  };

  // Add default props
  DoorDelivery.defaultProps = {
    onUpdateDeliveryDetails: () => {}
  };

  // Save to localStorage whenever form fields change
  useEffect(() => {
    console.log('DoorDelivery: Saving form data to localStorage', {
      address,
      building,
      name,
      email,
      age,
      phone,
      instructions
    });

    localStorage.setItem('delivery_address', address);
    localStorage.setItem('delivery_building', building);
    localStorage.setItem('delivery_name', name);
    localStorage.setItem('delivery_email', email);
    localStorage.setItem('delivery_age', age);
    localStorage.setItem('delivery_phone', phone);
    localStorage.setItem('delivery_instructions', instructions);
  }, [address, building, name, email, age, phone, instructions]);

  // Add another useEffect to load data when component mounts
  useEffect(() => {
    console.log('DoorDelivery: Component mounted, checking for login email');

    // Check all possible sources for login email in priority order
    const loginEmail =
			// First check our specific login email storage
			localStorage.getItem('current_login_email') ||
			// Then check Redux auth states
			authState?.user?.email ||
			patientAuthState?.user?.email ||
			// Then session/local storage options
			JSON.parse(sessionStorage.getItem('authPayload') || '{}').email ||
			JSON.parse(localStorage.getItem('userData') || '{}').email ||
			JSON.parse(localStorage.getItem('loginData') || '{}').email ||
			// Fall back to existing delivery email
			localStorage.getItem('delivery_email') ||
			// Keep current value if no email found
			email;

    console.log('Login email sources checked, using:', loginEmail);

    if (loginEmail) {
      console.log('Setting email from login data:', loginEmail);
      setEmail(loginEmail);
      localStorage.setItem('delivery_email', loginEmail);
    }
  }, []);

  // Then add this useEffect to update authentication status
  useEffect(() => {
    // Check authentication from Redux
    const authenticated = Boolean(
      authState?.isAuthenticated || patientAuthState?.isAuthenticated
    );

    // Update state
    setIsAuthenticated(authenticated);
    console.log('DoorDelivery: Authentication status:', authenticated);
  }, [authState, patientAuthState]);

  const validateForm = () => {
    if (!email?.trim()) throw new Error('Email is required');
    if (!phone?.trim()) throw new Error('Phone is required');
    if (!name?.trim()) throw new Error('Name is required');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }
    if (!age?.trim()) throw new Error('Age is required');
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 18) throw new Error('Must be 18 or older');
  };

  const handlePlaceSelect = (place) => {
    setSelectedPlace(place);
    setAddress(place.formatted_address);
  };

  const data = {
    address,
    building,
    name,
    email,
    age,
    phone,
    instructions,
    location: selectedPlace?.geometry?.location
      ? {
          lat: selectedPlace.geometry.location.lat(),
          lng: selectedPlace.geometry.location.lng()
        }
      : null,
    place_name: selectedPlace?.name || ''
  };

  try {
    // API call here
  } catch (error) {
    // Error handling
  }

  // Validate prop on mount
  React.useEffect(() => {
    if (typeof onUpdateDeliveryDetails !== 'function') {
      console.error('onUpdateDeliveryDetails must be a function');
    }
  }, [onUpdateDeliveryDetails]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      validateForm();
      const deliveryData = {
        shipping_address: address?.trim(),
        phone_number: phone?.trim(),
        email: email?.trim(),
        name: name?.trim(),
        age: age?.trim(),
        building: building?.trim(),
        instructions: instructions?.trim()
      };

      // Save to localStorage
      localStorage.setItem('deliveryFormData', JSON.stringify(deliveryData));

      if (onUpdateDeliveryDetails) {
        onUpdateDeliveryDetails(deliveryData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mt='2rem' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <form onSubmit={handleFormSubmit}>
        <AddressAutocomplete onPlaceSelect={handlePlaceSelect} />
        <TextField
          label='Apartment/Building/House Number'
          variant='outlined'
          fullWidth
          value={building}
          required
          placeholder='Apartment/Building/House Number'
          onChange={(e) => setBuilding(e.target.value)}
          sx={{
					  marginTop: '1rem',
					  '& .MuiInputBase-input': { color: 'white' },
					  '& .MuiInputBase-input::placeholder': { color: 'black' }
          }}
        />
        <TextField
          label='Enter your Name'
          variant='outlined'
          fullWidth
          value={name}
          required
          placeholder={loading ? 'Loading...' : 'Enter your Name'}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
          InputProps={{
					  startAdornment: loading && <CircularProgress size={20} />
          }}
          sx={{
					  marginTop: '1rem',
					  '& .MuiInputBase-input': { color: 'white' },
					  '& .MuiInputBase-input::placeholder': { color: 'black' }
          }}
        />
        <Box
          sx={{
					  display: 'flex',
					  flexDirection: { xs: 'column', md: 'row' },
					  gap: 2,
					  marginTop: '1rem'
          }}
        >
          <TextField
            label={`Email ${isAuthenticated ? '(Authenticated)' : '(Not Authenticated)'}`}
            variant='outlined'
            fullWidth
            value={email}
            required
            placeholder={loading ? 'Loading...' : 'Email'}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            InputProps={{
						  startAdornment: loading && <CircularProgress size={20} />,
						  endAdornment: (
  <Box
    sx={{
									  display: 'flex',
									  alignItems: 'center',
									  marginLeft: 1,
									  color: isAuthenticated ? 'green' : 'orange'
    }}
  >
    {isAuthenticated ? '✓ Verified' : '⚠️ Login required'}
  </Box>
						  )
            }}
            helperText={
							isAuthenticated
							  ? "You're logged in with this email"
							  : 'Please verify your account by logging in'
						}
            sx={{
						  '& .MuiInputBase-input': { color: 'white' },
						  '& .MuiInputBase-input::placeholder': { color: 'black' },
						  '& .MuiFormHelperText-root': {
						    color: isAuthenticated ? 'green' : 'orange',
						    fontWeight: 500
              }
            }}
          />
          <TextField
            label='Age'
            variant='outlined'
            fullWidth
            type='number'
            value={age}
            required
            placeholder={loading ? 'Loading...' : 'Enter your Age'}
            onChange={(e) => setAge(e.target.value)}
            disabled={loading}
            InputProps={{
						  startAdornment: loading && <CircularProgress size={20} />,
						  inputProps: { min: 18 }
            }}
            sx={{
						  marginTop: '1rem',
						  '& .MuiInputBase-input': { color: 'white' },
						  '& .MuiInputBase-input::placeholder': { color: 'black' }
            }}
          />
          <TextField
            label='Phone Number / Delivery Number'
            variant='outlined'
            fullWidth
            value={phone}
            required
            placeholder={
							loading ? 'Loading...' : 'Phone Number / Delivery Number'
						}
            onChange={(e) => setPhone(e.target.value)}
            disabled={loading}
            InputProps={{
						  startAdornment: loading && <CircularProgress size={20} />
            }}
            sx={{
						  '& .MuiInputBase-input': { color: 'white' },
						  '& .MuiInputBase-input::placeholder': { color: 'black' }
            }}
          />
        </Box>
        <TextField
          label='Delivery Instructions'
          variant='outlined'
          fullWidth
          multiline
          rows={4}
          value={instructions}
          placeholder='Delivery Instructions'
          onChange={(e) => setInstructions(e.target.value)}
          sx={{
					  marginTop: '1rem',
					  '& .MuiInputBase-input': { color: 'white' },
					  '& .MuiInputBase-input::placeholder': { color: 'black' }
          }}
        />
        <Button
          type='submit'
          variant='contained'
          color='primary'
          fullWidth
          sx={{ mt: 2 }}
        >
          Save Delivery Details
        </Button>
      </form>
      {/* {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>} */}
    </Box>
  );
}

DoorDelivery.propTypes = {
  onUpdateDeliveryDetails: PropTypes.func.isRequired
};
export default DoorDelivery;
